import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ShieldCheck, Users, FileCheck, AlertTriangle, CheckCircle, Clock, Send, Trash2, Eye, Loader2, ChevronLeft } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const cfg = {
    'Valid': 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    'Expiring Soon': 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    'Expired': 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg[status] || cfg['Valid']}`}>{status}</span>;
};

export const AdminPanelPage = () => {
  usePageMeta({
    title: 'Admin Panel',
    description: 'Digitive Health admin dashboard for managing users and credentials.',
    path: '/dh-admin-panel',
    noindex: true,
  });

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCredentials, setUserCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ title: '', message: '', send_email: true });
  const [notifyStatus, setNotifyStatus] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/auth?redirect=/dh-admin-panel');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchStats();
      fetchUsers();
    }
  }, [user]);

  const apiFetch = async (url, opts = {}) => {
    return fetch(`${API_URL}${url}`, { credentials: 'include', ...opts });
  };

  const fetchStats = async () => {
    const res = await apiFetch('/api/admin/stats');
    if (res.ok) setStats(await res.json());
  };

  const fetchUsers = async () => {
    const res = await apiFetch('/api/admin/users');
    if (res.ok) setUsers(await res.json());
  };

  const viewUser = async (uid) => {
    setLoading(true);
    const res = await apiFetch(`/api/admin/users/${uid}`);
    if (res.ok) {
      const data = await res.json();
      setSelectedUser(data.user);
      setUserCredentials(data.credentials);
      setView('user-detail');
    }
    setLoading(false);
  };

  const deleteCredential = async (credId) => {
    await apiFetch(`/api/admin/credentials/${credId}`, { method: 'DELETE' });
    setUserCredentials(prev => prev.filter(c => c.id !== credId));
    fetchStats();
  };

  const sendNotification = async (userId) => {
    if (!notifyForm.title || !notifyForm.message) return;
    setNotifyStatus('sending');
    const res = await apiFetch('/api/admin/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, ...notifyForm }),
    });
    if (res.ok) {
      setNotifyStatus('sent');
      setNotifyForm({ title: '', message: '', send_email: true });
      setTimeout(() => setNotifyStatus(''), 3000);
    } else {
      setNotifyStatus('error');
    }
  };

  const deleteUser = async (userId) => {
    setLoading(true);
    const res = await apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
    if (res.ok) {
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      setDeleteConfirm(null);
      if (view === 'user-detail') {
        setView('dashboard');
        setSelectedUser(null);
      }
      fetchStats();
    }
    setLoading(false);
  };

  if (authLoading || !user || user.role !== 'admin') {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel-page">
      <Header />
      <section className="pt-28 pb-12 bg-secondary">
        <div className="container-width">
          <div className="inline-flex items-center gap-2 bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-1 rounded-full mb-4 text-xs font-medium">
            <ShieldCheck className="h-3.5 w-3.5" />
            Admin Panel
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">Credential Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage users, credentials, and notifications</p>
        </div>
      </section>

      <section className="py-8">
        <div className="container-width max-w-6xl">
          {view === 'dashboard' && (
            <>
              {/* Stats */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-stats">
                  <Card className="p-4 text-center border-border/50">
                    <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                    <p className="text-2xl font-bold text-foreground">{stats.total_users}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Users</p>
                  </Card>
                  <Card className="p-4 text-center border-border/50">
                    <FileCheck className="h-5 w-5 text-primary mx-auto mb-1" />
                    <p className="text-2xl font-bold text-foreground">{stats.total_credentials}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Credentials</p>
                  </Card>
                  <Card className="p-4 text-center border-emerald-500/20 bg-emerald-500/5">
                    <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.valid}</p>
                    <p className="text-[10px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Active</p>
                  </Card>
                  <Card className="p-4 text-center border-amber-500/20 bg-amber-500/5">
                    <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.expiring_soon}</p>
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 uppercase tracking-wider">Expiring</p>
                  </Card>
                  <Card className="p-4 text-center border-red-500/20 bg-red-500/5">
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.expired}</p>
                    <p className="text-[10px] text-red-600 dark:text-red-400 uppercase tracking-wider">Expired</p>
                  </Card>
                </div>
              )}

              {/* Users Table */}
              <Card className="border-border/50 overflow-hidden" data-testid="admin-users-table">
                <div className="px-6 py-4 border-b border-border/50">
                  <h2 className="text-base font-bold text-foreground">All Users ({users.length})</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary/50">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Provider</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Joined</th>
                        <th className="text-right px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {users.map(u => (
                        <tr key={u.user_id} className="hover:bg-secondary/30 transition-colors" data-testid={`admin-user-${u.user_id}`}>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                {u.name?.[0]?.toUpperCase() || '?'}
                              </div>
                              <span className="text-sm font-medium text-foreground">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-sm text-muted-foreground">{u.email}</td>
                          <td className="px-6 py-3">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-primary/10 text-primary'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm text-muted-foreground capitalize">{u.auth_provider}</td>
                          <td className="px-6 py-3 text-xs text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => viewUser(u.user_id)} className="text-primary hover:text-primary" data-testid={`view-user-${u.user_id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              {u.role !== 'admin' && (
                                <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(u)} className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-user-${u.user_id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* User Detail View */}
          {view === 'user-detail' && selectedUser && (
            <>
              <Button variant="ghost" onClick={() => { setView('dashboard'); setSelectedUser(null); }} className="mb-4 text-muted-foreground" data-testid="admin-back-btn">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back to Users
              </Button>

              <div className="grid md:grid-cols-3 gap-6">
                {/* User Info */}
                <Card className="p-6 border-border/50" data-testid="admin-user-info">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-xl font-bold text-primary mb-4">
                    {selectedUser.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <h2 className="text-lg font-bold text-foreground">{selectedUser.name}</h2>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                  <div className="flex gap-2 mt-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${selectedUser.role === 'admin' ? 'bg-red-500/10 text-red-600' : 'bg-primary/10 text-primary'}`}>{selectedUser.role}</span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">{selectedUser.auth_provider}</span>
                  </div>

                  {selectedUser.role !== 'admin' && (
                    <Button variant="outline" onClick={() => setDeleteConfirm(selectedUser)} className="w-full mt-4 text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/10 text-sm h-9" data-testid="admin-delete-user-btn">
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete User
                    </Button>
                  )}

                  {/* Send Notification */}
                  <div className="mt-6 pt-6 border-t border-border/50">
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2"><Send className="h-4 w-4" /> Send Notification</h3>
                    <div className="space-y-3">
                      <Input value={notifyForm.title} onChange={e => setNotifyForm({ ...notifyForm, title: e.target.value })} placeholder="Title" className="h-9 text-sm" data-testid="admin-notify-title" />
                      <textarea
                        value={notifyForm.message}
                        onChange={e => setNotifyForm({ ...notifyForm, message: e.target.value })}
                        placeholder="Message..."
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid="admin-notify-message"
                      />
                      <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <input type="checkbox" checked={notifyForm.send_email} onChange={e => setNotifyForm({ ...notifyForm, send_email: e.target.checked })} className="rounded" data-testid="admin-notify-email-check" />
                        Also send via email (SendGrid)
                      </label>
                      <Button onClick={() => sendNotification(selectedUser.user_id)} disabled={notifyStatus === 'sending'} className="w-full bg-primary text-white rounded-lg text-sm h-9" data-testid="admin-notify-send-btn">
                        {notifyStatus === 'sending' ? <Loader2 className="h-3 w-3 animate-spin" /> : notifyStatus === 'sent' ? 'Sent!' : 'Send Notification'}
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Credentials */}
                <div className="md:col-span-2">
                  <Card className="border-border/50 overflow-hidden" data-testid="admin-user-credentials">
                    <div className="px-6 py-4 border-b border-border/50">
                      <h2 className="text-base font-bold text-foreground">Credentials ({userCredentials.length})</h2>
                    </div>
                    {userCredentials.length === 0 ? (
                      <div className="px-6 py-12 text-center text-sm text-muted-foreground">No credentials for this user.</div>
                    ) : (
                      <div className="divide-y divide-border/30">
                        {userCredentials.map(c => (
                          <div key={c.id} className="px-6 py-4 flex items-center gap-4 hover:bg-secondary/30" data-testid={`admin-cred-${c.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-medium text-foreground">{c.credential_type}</span>
                                <StatusBadge status={c.status} />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {c.credential_number && `#${c.credential_number} `}
                                {c.issuing_state && `| ${c.issuing_state} `}
                                | Expires {new Date(c.expiration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => deleteCredential(c.id)} className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400 shrink-0" data-testid={`admin-delete-cred-${c.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-user-modal">
          <Card className="p-6 max-w-sm w-full mx-4 border-border/50 shadow-2xl">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-foreground text-center mb-1">Delete User</h3>
            <p className="text-sm text-muted-foreground text-center mb-1">
              Are you sure you want to delete <span className="font-semibold text-foreground">{deleteConfirm.name}</span>?
            </p>
            <p className="text-xs text-muted-foreground text-center mb-6">
              ({deleteConfirm.email}) — This will permanently remove the user, their credentials, and notifications.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 h-10 rounded-lg" data-testid="delete-user-cancel-btn">Cancel</Button>
              <Button onClick={() => deleteUser(deleteConfirm.user_id)} disabled={loading} className="flex-1 h-10 rounded-lg bg-red-600 hover:bg-red-700 text-white" data-testid="delete-user-confirm-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminPanelPage;
